import { motion } from "framer-motion";
import { CalendarCheck, ChartLineUp, GraduationCap, Martini, Champagne, Truck, ArrowRight } from "@phosphor-icons/react";
import { IMAGES } from "../data";
import { useLang } from "../i18n/LanguageContext";

const SERVICE_META = [
  { id: "consulting", icon: ChartLineUp, image: IMAGES.consulting },
  { id: "schulungen", icon: GraduationCap, image: IMAGES.training },
  { id: "events", icon: CalendarCheck, image: IMAGES.events },
  { id: "cocktail", icon: Martini, image: IMAGES.cocktail },
  { id: "cocktailkurse", icon: Champagne, image: IMAGES.cocktailkurs },
  { id: "verleih", icon: Truck, image: IMAGES.equipment, ctaHref: "#equipment" },
];

function ServiceBlock({ meta, content, index, ctaPrefix }) {
  const Icon = meta.icon;
  const reversed = index % 2 === 1;
  const ctaLabel = content.ctaLabel || `${ctaPrefix} ${content.tag}`;
  return (
    <motion.article
      data-testid={`service-block-${meta.id}`}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center"
    >
      <div className={`relative ${reversed ? "lg:order-2" : ""}`}>
        <div className="overflow-hidden rounded-3xl border border-gray-200 group">
          <img
            src={meta.image}
            alt={content.title}
            className="w-full h-[300px] sm:h-[420px] object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        <span className="absolute -top-5 left-6 font-heading text-6xl sm:text-7xl font-black text-[#1e2a6e]/10 select-none">
          0{index + 1}
        </span>
      </div>

      <div className={reversed ? "lg:order-1" : ""}>
        <div className="flex items-center gap-3 mb-5">
          <span className="w-12 h-12 rounded-xl bg-[#1e2a6e] flex items-center justify-center">
            <Icon size={26} weight="duotone" className="text-white" />
          </span>
          <span className="font-accent text-xs tracking-[0.2em] uppercase font-bold text-[#4a90d9]">
            {content.tag}
          </span>
        </div>
        <h3 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight text-[#0A0D1F]">
          {content.title}
        </h3>
        <p className="text-base sm:text-lg text-[#5C6479] mt-4 leading-relaxed max-w-xl">{content.text}</p>
        <ul className="mt-6 space-y-2.5">
          {content.points.map((p) => (
            <li key={p} className="flex items-center gap-3 text-[#0A0D1F] font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4a90d9]" />
              {p}
            </li>
          ))}
        </ul>
        <a
          href={meta.ctaHref || "#kontakt"}
          data-testid={`service-cta-${meta.id}`}
          className="group mt-8 inline-flex items-center gap-2 text-[#1e2a6e] font-semibold hover:text-[#4a90d9] transition-colors duration-200"
        >
          {ctaLabel}
          <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform duration-200" />
        </a>
      </div>
    </motion.article>
  );
}

export default function Leistungen() {
  const { t } = useLang();
  const l = t.leistungen;
  return (
    <section id="leistungen" data-testid="leistungen-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-16 sm:mb-24">
          <span className="font-accent text-xs tracking-[0.2em] uppercase font-bold text-[#4a90d9]">
            {l.overline}
          </span>
          <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight text-[#0A0D1F] mt-4">
            {l.title}
          </h2>
          <p className="text-base sm:text-lg text-[#5C6479] mt-5 leading-relaxed">{l.subtitle}</p>
        </div>

        <div className="space-y-20 sm:space-y-28">
          {SERVICE_META.map((meta, i) => (
            <ServiceBlock
              key={meta.id}
              meta={meta}
              content={l.services[meta.id]}
              index={i}
              ctaPrefix={l.ctaPrefix}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
